import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { Button, Box, TextField, FormControl, Select, MenuItem, Typography, Divider, Radio, RadioGroup, FormControlLabel } from "@material-ui/core";
import MainContainer from "../../../components/MainContainer";
import { headerData } from "../../../data/modules/dashboard-header";
import { userDetailContext } from "../../../App";
import { useContext } from "react";
import TimeTrackingService from "../../../services/TimeTrackingService";
import ImmobilisationService from "../../../services/ImmobilisationService";
import PlanningService from "../../../services/PlanningService";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import ServiceService from "../../../services/ServiceService";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import EventIcon from '@mui/icons-material/Event';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIconButton from '@mui/icons-material/Search';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DateRangeIcon from '@mui/icons-material/DateRange';
import TodayIcon from '@mui/icons-material/Today';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GardianService from "../../../services/GardianService";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

import logo from '../../../assets/images/fid_logo.png';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    elevation: 0,
  },
  labelPosition: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'block',
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
  },
  filterSection: {
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: '1px solid #e0e0e0',
  },
  filterTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: '2px solid #1976d2',
    width: 'fit-content',
  },
  filterTitleIcon: {
    color: '#1976d2',
    fontSize: '1.2rem',
  },
  filterTitleText: {
    fontWeight: 700,
    color: '#1976d2',
    fontSize: '1rem',
    letterSpacing: '0.5px',
  },
  searchButton: {
    height: '56px',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  exportButton: {
    height: '56px',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
    backgroundColor: '#2e7d32',
    '&:hover': {
      backgroundColor: '#1b5e20',
    },
  },
  pdfButton: {
    height: '56px',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
    backgroundColor: '#d32f2f',
    '&:hover': {
      backgroundColor: '#b71c1c',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  selectField: {
    backgroundColor: '#ffffff',
  },
  tableHeader: {
    backgroundColor: '#808080',
    '& th': {
      color: '#ffffff',
      fontWeight: 700,
      fontSize: '0.875rem',
    },
  },
  radioGroup: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#ffffff',
    borderRadius: theme.spacing(1),
  },
  dateField: {
    marginTop: theme.spacing(1),
  },
}));

export default function TimeTrackingGardian(props) {
  const classes = useStyles();
  // ✅ Conversion en nombre pour que la comparaison === 1 fonctionne
  const directionUser = Number(localStorage.getItem("USER_DIR")) || 1;
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [direction, setDirection] = useState([]);
  const [services, setServices] = useState([]);
  const [rows, setRows] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const contextData = useContext(userDetailContext);

  // ✅ On peut changer de direction UNIQUEMENT si USER_DIR === 1
  const canChangeDirection = directionUser === 1;

  // Type de recherche: "period" (année/mois) ou "range" (date début/fin)
  const [searchType, setSearchType] = useState("period");

  // Années disponibles (de 2026 à l'année actuelle)
  const currentYear = new Date().getFullYear();
  const startYear = 2026;
  const years = [];
  for (let year = startYear; year <= currentYear; year++) {
    years.push(year);
  }

  // Mois disponibles
  const months = [
    { value: 1, label: "Janvier" },
    { value: 2, label: "Février" },
    { value: 3, label: "Mars" },
    { value: 4, label: "Avril" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Juin" },
    { value: 7, label: "Juillet" },
    { value: 8, label: "Août" },
    { value: 9, label: "Septembre" },
    { value: 10, label: "Octobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Décembre" },
  ];

  // Noms des mois en français pour le formatage
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const [filters, setFilters] = useState({
    direction_id: directionUser,
    service_id: 0,
    type: "1",
    year: currentYear,
    month: new Date().getMonth() + 1,
    date_debut: "",
    date_fin: "",
  });

  const handleChanges = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchTypeChange = (event) => {
    setSearchType(event.target.value);
    if (event.target.value === "period") {
      setFilters(prev => ({ ...prev, date_debut: "", date_fin: "" }));
    } else {
      setFilters(prev => ({ ...prev, year: currentYear, month: new Date().getMonth() + 1 }));
    }
  };

  const handleSearch = async () => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      let result;

      if (searchType === "period") {
        result = await GardianService.gettimetracking(
          filters.direction_id,
          filters.service_id,
          filters.year,
          filters.month,
          filters.type
        );
      } else {
        if (!filters.date_debut || !filters.date_fin) {
          alert("Veuillez sélectionner une date de début et une date de fin");
          setOpenLoadingSimpleBackdrop(false);
          return;
        }

        if (new Date(filters.date_debut) > new Date(filters.date_fin)) {
          alert("La date de début doit être antérieure à la date de fin");
          setOpenLoadingSimpleBackdrop(false);
          return;
        }

        result = await GardianService.gettimetracking(
          filters.direction_id,
          filters.service_id,
          filters.date_debut,
          filters.date_fin,
          filters.type
        );
      }

      if (result && result.data) {
        setRows(result.data);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching time tracking data:", error);
      setRows([]);
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const getReportTitle = () => {
    if (searchType === "period") {
      const monthName = months.find(m => m.value === filters.month)?.label || filters.month;
      return `SITUATION PRESENCE DE LA DIRECTION GÉNÉRALE DU ${filters.year}-${String(filters.month).padStart(2, '0')}`;
    } else {
      return `SITUATION PRESENCE DE LA DIRECTION GÉNÉRALE DU ${filters.date_debut} AU ${filters.date_fin}`;
    }
  };

  const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    return `Fait à Antananarivo, le ${day} ${month} ${year}`;
  };

  const formatDateWithMonthName = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleExportPDF = async () => {
    if (rows.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    setPdfLoading(true);
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();

      const logoImg = new Image();
      logoImg.src = logo;

      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
      });

      try {
        doc.addImage(logoImg, 'PNG', 5, 5, 35, 18);
      } catch (e) {
        console.warn("Logo non trouvé, continuation sans logo");
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const title = getReportTitle();
      const titleWidth = doc.getStringUnitWidth(title) * 14 / doc.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      doc.text(title, titleX, 15);

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(5, 20, pageWidth - 5, 20);

      const tableColumn = [
        'N°', 'Direction', 'Service', 'Fonction', 'Matricule',
        'Noms et prénoms', 'Congé', 'Pointage Manuel', 'Présent',
        'Jours de repos', 'Absent', 'Total'
      ];

      const tableRows = rows.map((row, index) => [
        index + 1,
        row.direction_code || '-',
        row.service_code || '-',
        row.function_name || '-',
        row.registration_number || '-',
        row.nom || '-',
        row.conge_valide || 0,
        row.timetracking_valide || 0,
        row.present_number || 0,
        row.weekend_number || 0,
        row.absent_number || 0,
        row.total_days || 0
      ]);

      const totalConge = rows.reduce((sum, row) => sum + (row.conge_valide || 0), 0);
      const totalTimetracking = rows.reduce((sum, row) => sum + (row.timetracking_valide || 0), 0);
      const totalPresent = rows.reduce((sum, row) => sum + (row.present_number || 0), 0);
      const totalWeekend = rows.reduce((sum, row) => sum + (row.weekend_number || 0), 0);
      const totalAbsent = rows.reduce((sum, row) => sum + (row.absent_number || 0), 0);
      const totalAll = rows.reduce((sum, row) => sum + (row.total_days || 0), 0);

      tableRows.push(['', '', '', '', '', 'TOTAUX :', totalConge, totalTimetracking, totalPresent, totalWeekend, totalAbsent, totalAll]);

      const marginLeft = 5;
      const marginRight = 5;
      const tableWidth = pageWidth - marginLeft - marginRight;

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 24,
        theme: 'grid',
        margin: { left: marginLeft, right: marginRight },
        tableWidth: tableWidth,
        headStyles: {
          fillColor: [128, 128, 128],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
        },
        bodyStyles: {
          fontSize: 7,
          cellPadding: 1.5,
        },
        columnStyles: {
          0: { cellWidth: 'auto', halign: 'center' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 'auto' },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 'auto' },
          6: { cellWidth: 'auto', halign: 'center' },
          7: { cellWidth: 'auto', halign: 'center' },
          8: { cellWidth: 'auto', halign: 'center' },
          9: { cellWidth: 'auto', halign: 'center' },
          10: { cellWidth: 'auto', halign: 'center' },
          11: { cellWidth: 'auto', halign: 'center' },
        },
        didParseCell: function (data) {
          if (data.row.index === tableRows.length - 1) {
            data.cell.styles.fillColor = [245, 245, 245];
            data.cell.styles.fontStyle = 'bold';
          }
          if (data.column.index === 6) data.cell.styles.textColor = [25, 118, 210];
          else if (data.column.index === 7) data.cell.styles.textColor = [46, 125, 50];
          else if (data.column.index === 8) data.cell.styles.textColor = [46, 125, 50];
          else if (data.column.index === 9) data.cell.styles.textColor = [2, 136, 209];
          else if (data.column.index === 10) data.cell.styles.textColor = [237, 108, 2];
          else if (data.column.index === 11) data.cell.styles.textColor = [156, 39, 176];
        },
        didDrawPage: function () {
          const footerY = doc.internal.pageSize.getHeight() - 8;
          doc.setFontSize(7);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - 20, footerY);
        }
      });

      const finalY = doc.internal.pageSize.getHeight() - 8;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(80, 80, 80);
      doc.text(getCurrentDate(), pageWidth - 80, finalY);

      let fileName;
      if (searchType === "period") {
        const monthName = months.find(m => m.value === filters.month)?.label || filters.month;
        fileName = `suivi_presence_${filters.year}_${monthName}.pdf`;
      } else {
        const dateDebutFormatted = formatDateWithMonthName(filters.date_debut);
        const dateFinFormatted = formatDateWithMonthName(filters.date_fin);
        fileName = `suivi_presence_${dateDebutFormatted}_au_${dateFinFormatted}.pdf`;
      }
      doc.save(fileName);

    } catch (error) {
      console.error("Erreur lors de l'export PDF:", error);
      alert("Une erreur est survenue lors de l'export du PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (rows.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    setExportLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Suivi de présence');

      try {
        const logoImg = new Image();
        logoImg.src = logo;
        await new Promise((resolve) => {
          logoImg.onload = resolve;
          logoImg.onerror = resolve;
        });

        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(logoImg, 0, 0, 200, 100);
        const dataUrl = canvas.toDataURL('image/png');

        const imageId = workbook.addImage({
          base64: dataUrl.split(',')[1],
          extension: 'png',
        });

        worksheet.addImage(imageId, {
          tl: { col: 0, row: 0 },
          ext: { width: 120, height: 60 }
        });
      } catch (e) {
        console.warn("Logo non trouvé pour Excel");
      }

      worksheet.mergeCells('A1:M2');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = getReportTitle();
      titleCell.font = { bold: true, size: 16, name: 'Arial' };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 40;

      worksheet.addRow([]);

      worksheet.columns = [
        { header: 'N°', key: 'index', width: 8 },
        { header: 'Direction', key: 'direction', width: 25 },
        { header: 'Service', key: 'service', width: 25 },
        { header: 'Fonction', key: 'function_name', width: 25 },
        { header: 'Matricule', key: 'registration_number', width: 15 },
        { header: 'Noms et prénoms', key: 'full_name', width: 30 },
        { header: 'Congé', key: 'conge_valide', width: 12 },
        { header: 'Pointage Manuel', key: 'timetracking_valide', width: 15 },
        { header: 'Présent', key: 'present_number', width: 12 },
        { header: 'Jours de repos', key: 'weekend_number', width: 15 },
        { header: 'Absent sans motif', key: 'absent_number', width: 15 },
        { header: 'Total général', key: 'total_days', width: 15 },
        { header: 'Observation', key: 'observation', width: 25 },
      ];

      const headerRow = worksheet.getRow(4);
      headerRow.height = 30;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '808080' }
        };
        cell.font = { bold: true, color: { argb: 'FFFFFF' }, size: 11, name: 'Calibri' };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
        };
      });

      rows.forEach((row, index) => {
        const rowData = worksheet.addRow({
          index: index + 1,
          direction: row.direction_code || '-',
          service: row.service_code || '-',
          function_name: row.function_name || '-',
          registration_number: row.registration_number || '-',
          full_name: row.nom || '-',
          conge_valide: row.conge_valide || 0,
          timetracking_valide: row.timetracking_valide || 0,
          present_number: row.present_number || 0,
          weekend_number: row.weekend_number || 0,
          absent_number: row.absent_number || 0,
          total_days: row.total_days || 0,
          observation: row.observation || '',
        });

        rowData.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } },
          };
          cell.alignment = { vertical: 'middle' };

          if ([7, 8, 9, 10, 11, 12].includes(colNumber)) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
            cell.font = { bold: true };
          }

          if (colNumber === 7) cell.font = { bold: true, color: { argb: '1976D2' } };
          else if (colNumber === 8) cell.font = { bold: true, color: { argb: '2E7D32' } };
          else if (colNumber === 9) cell.font = { bold: true, color: { argb: '2E7D32' } };
          else if (colNumber === 10) cell.font = { bold: true, color: { argb: '0288D1' } };
          else if (colNumber === 11) cell.font = { bold: true, color: { argb: 'ED6C02' } };
          else if (colNumber === 12) cell.font = { bold: true, color: { argb: '9C27B0' } };
        });
      });

      const totalRow = worksheet.addRow([]);

      const totalConge = rows.reduce((sum, row) => sum + (row.conge_valide || 0), 0);
      const totalTimetracking = rows.reduce((sum, row) => sum + (row.timetracking_valide || 0), 0);
      const totalPresent = rows.reduce((sum, row) => sum + (row.present_number || 0), 0);
      const totalWeekend = rows.reduce((sum, row) => sum + (row.weekend_number || 0), 0);
      const totalAbsent = rows.reduce((sum, row) => sum + (row.absent_number || 0), 0);
      const totalAll = rows.reduce((sum, row) => sum + (row.total_days || 0), 0);

      totalRow.getCell(1).value = '';
      totalRow.getCell(2).value = '';
      totalRow.getCell(3).value = '';
      totalRow.getCell(4).value = '';
      totalRow.getCell(5).value = '';
      totalRow.getCell(6).value = 'TOTAUX :';
      totalRow.getCell(7).value = totalConge;
      totalRow.getCell(8).value = totalTimetracking;
      totalRow.getCell(9).value = totalPresent;
      totalRow.getCell(10).value = totalWeekend;
      totalRow.getCell(11).value = totalAbsent;
      totalRow.getCell(12).value = totalAll;
      totalRow.getCell(13).value = '';

      totalRow.eachCell((cell, colNumber) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F5F5F5' }
        };
        cell.font = { bold: true };
        cell.border = {
          top: { style: 'thin', color: { argb: '000000' } },
          left: { style: 'thin', color: { argb: '000000' } },
          bottom: { style: 'thin', color: { argb: '000000' } },
          right: { style: 'thin', color: { argb: '000000' } },
        };
        cell.alignment = { vertical: 'middle' };

        if (colNumber === 6) {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }
        if ([7, 8, 9, 10, 11, 12].includes(colNumber)) {
          cell.alignment = { horizontal: 'right', vertical: 'middle' };
          if (colNumber === 7) cell.font = { bold: true, color: { argb: '1976D2' } };
          else if (colNumber === 8) cell.font = { bold: true, color: { argb: '2E7D32' } };
          else if (colNumber === 9) cell.font = { bold: true, color: { argb: '2E7D32' } };
          else if (colNumber === 10) cell.font = { bold: true, color: { argb: '0288D1' } };
          else if (colNumber === 11) cell.font = { bold: true, color: { argb: 'ED6C02' } };
          else if (colNumber === 12) cell.font = { bold: true, color: { argb: '9C27B0' } };
        }
      });

      const footerRow = worksheet.addRow([]);
      footerRow.getCell(6).value = getCurrentDate();
      footerRow.getCell(6).font = { italic: true, size: 10, name: 'Calibri' };
      worksheet.mergeCells(`F${worksheet.rowCount}:M${worksheet.rowCount}`);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      let fileName;
      if (searchType === "period") {
        const monthName = months.find(m => m.value === filters.month)?.label || filters.month;
        fileName = `suivi_presence_${filters.year}_${monthName}.xlsx`;
      } else {
        const dateDebutFormatted = formatDateWithMonthName(filters.date_debut);
        const dateFinFormatted = formatDateWithMonthName(filters.date_fin);
        fileName = `suivi_presence_${dateDebutFormatted}_au_${dateFinFormatted}.xlsx`;
      }
      saveAs(blob, fileName);

    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      alert("Une erreur est survenue lors de l'export des données");
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    ImmobilisationService.direction().then((result) => {
      if (result && result.data) {
        setDirection(result.data);
      }
    });
  }, []);

  useEffect(() => {
    const fetchServices = async (direction_id) => {
      if (direction_id === 0 || direction_id === "") {
        ServiceService.getService().then((results) => {
          if (results && results.data) {
            setServices(results.data);
          }
        });
      } else if (direction_id !== 0 && direction_id !== "") {
        PlanningService.Direction(direction_id).then((results) => {
          if (results && results.data) {
            setServices(results.data);
          }
        });
      }
    };
    fetchServices(filters.direction_id);
  }, [filters.direction_id]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainContainer {...headerData}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

      <Box className={classes.filterSection}>

        <Box className={classes.filterTitle}>
          <BusinessIcon className={classes.filterTitleIcon} />
          <Typography className={classes.filterTitleText}>
            INFORMATIONS PROFESSIONNELLES
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <label className={classes.labelPosition}>Direction</label>
            <FormControl fullWidth variant="outlined" size="medium" className={classes.selectField}>
              <Select
                labelId="direction-select-label"
                id="direction-select"
                name="direction_id"
                value={filters.direction_id}
                onChange={handleChanges}
                disabled={!canChangeDirection}
              >
                {canChangeDirection && (
                  <MenuItem value={0}>Toutes les directions</MenuItem>
                )}
                {direction.map((row) => (
                  <MenuItem key={row.direction_id} value={row.direction_id}>
                    {row.name} ({row.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <label className={classes.labelPosition}>Service</label>
            <FormControl fullWidth variant="outlined" size="medium" className={classes.selectField}>
              <Select
                labelId="service-select-label"
                id="service-select"
                name="service_id"
                value={filters.service_id}
                onChange={handleChanges}
              >
                <MenuItem value={0}>Tous les services</MenuItem>
                {services.map((row) => (
                  <MenuItem key={row.service_id} value={row.service_id}>
                    {row.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box className={classes.filterTitle}>
          <DateRangeIcon className={classes.filterTitleIcon} />
          <Typography className={classes.filterTitleText}>
            TYPE DE RECHERCHE
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth className={classes.radioGroup}>
          <RadioGroup
            row
            value={searchType}
            onChange={handleSearchTypeChange}
          >
            <FormControlLabel
              value="period"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <TodayIcon fontSize="small" />
                  <span>Recherche par Année et Mois</span>
                </Box>
              }
            />
            <FormControlLabel
              value="range"
              control={<Radio />}
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRangeIcon fontSize="small" />
                  <span>Recherche par Période (Date début - Date fin)</span>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        <Box className={classes.filterTitle}>
          <EventIcon className={classes.filterTitleIcon} />
          <Typography className={classes.filterTitleText}>
            PÉRIODE DE RECHERCHE
          </Typography>
        </Box>

        {searchType === "period" ? (
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <label className={classes.labelPosition}>Année</label>
              <FormControl fullWidth variant="outlined" size="medium" className={classes.selectField}>
                <Select
                  labelId="year-select-label"
                  id="year-select"
                  name="year"
                  value={filters.year}
                  onChange={handleChanges}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <label className={classes.labelPosition}>Mois</label>
              <FormControl fullWidth variant="outlined" size="medium" className={classes.selectField}>
                <Select
                  labelId="month-select-label"
                  id="month-select"
                  name="month"
                  value={filters.month}
                  onChange={handleChanges}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  startIcon={<SearchIconButton />}
                  className={classes.searchButton}
                  fullWidth
                  size="large"
                >
                  RECHERCHER
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExportExcel}
                  startIcon={<FileDownloadIcon />}
                  className={classes.exportButton}
                  fullWidth
                  size="large"
                  disabled={exportLoading || rows.length === 0}
                >
                  {exportLoading ? "EXPORT..." : "EXPORT EXCEL"}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExportPDF}
                  startIcon={<PictureAsPdfIcon />}
                  className={classes.pdfButton}
                  fullWidth
                  size="large"
                  disabled={pdfLoading || rows.length === 0}
                >
                  {pdfLoading ? "PDF..." : "EXPORT PDF"}
                </Button>
              </div>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <label className={classes.labelPosition}>Date début</label>
              <TextField
                fullWidth
                type="date"
                name="date_debut"
                value={filters.date_debut}
                onChange={handleChanges}
                variant="outlined"
                size="medium"
                InputLabelProps={{ shrink: true }}
                className={classes.dateField}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <label className={classes.labelPosition}>Date fin</label>
              <TextField
                fullWidth
                type="date"
                name="date_fin"
                value={filters.date_fin}
                onChange={handleChanges}
                variant="outlined"
                size="medium"
                InputLabelProps={{ shrink: true }}
                className={classes.dateField}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <div className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  startIcon={<SearchIconButton />}
                  className={classes.searchButton}
                  fullWidth
                  size="large"
                >
                  RECHERCHER
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExportExcel}
                  startIcon={<FileDownloadIcon />}
                  className={classes.exportButton}
                  fullWidth
                  size="large"
                  disabled={exportLoading || rows.length === 0}
                >
                  {exportLoading ? "EXPORT..." : "EXPORT EXCEL"}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExportPDF}
                  startIcon={<PictureAsPdfIcon />}
                  className={classes.pdfButton}
                  fullWidth
                  size="large"
                  disabled={pdfLoading || rows.length === 0}
                >
                  {pdfLoading ? "PDF..." : "EXPORT PDF"}
                </Button>
              </div>
            </Grid>
          </Grid>
        )}
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} aria-label="time tracking table">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell align="center" width={60}>N°</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Fonction</TableCell>
              <TableCell>Matricule</TableCell>
              <TableCell>Noms et prénoms</TableCell>
              <TableCell align="center">Congé</TableCell>
              <TableCell align="center">Pointage Manuel</TableCell>
              <TableCell align="center">Présent</TableCell>
              <TableCell align="center">Jours de repos</TableCell>
              <TableCell align="center">Absent sans motif</TableCell>
              <TableCell align="center">Total général</TableCell>
              <TableCell align="center">Observation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? (
              <>
                {rows.map((row, index) => (
                  <TableRow key={row.id || index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell>{row.direction_code || '-'}</TableCell>
                    <TableCell>{row.service_code || '-'}</TableCell>
                    <TableCell>{row.function_name || '-'}</TableCell>
                    <TableCell>{row.registration_number || '-'}</TableCell>
                    <TableCell>{row.nom}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="primary">
                        {row.conge_valide || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {row.timetracking_valide || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {row.present_number || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="info.main">
                        {row.weekend_number || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="warning.main">
                        {row.absent_number || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="secondary.main">
                        {row.total_days || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600} color="secondary.main">
                        {row.observation || ""}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>
                    TOTAUX :
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {rows.reduce((sum, row) => sum + (row.conge_valide || 0), 0)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {rows.reduce((sum, row) => sum + (row.timetracking_valide || 0), 0)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {rows.reduce((sum, row) => sum + (row.present_number || 0), 0)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {rows.reduce((sum, row) => sum + (row.weekend_number || 0), 0)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {rows.reduce((sum, row) => sum + (row.absent_number || 0), 0)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                    {rows.reduce((sum, row) => sum + (row.total_days || 0), 0)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                  </TableCell>
                </TableRow>
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                  <AssignmentIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Aucune donnée trouvée
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Veuillez modifier vos critères de recherche
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </MainContainer>
  );
}